import type { AssistantState } from '../types';

const TRANSITIONS: Record<AssistantState, AssistantState[]> = {
  IDLE: ['INITIALIZING'],
  INITIALIZING: ['READY', 'ERROR'],
  READY: ['LISTENING', 'SPEAKING', 'PROCESSING', 'INITIALIZING', 'IDLE', 'ERROR'],
  // IDLE is reachable from every active state so close()/destroy() can always
  // return to a neutral state regardless of what the user was doing.
  LISTENING: ['PROCESSING', 'READY', 'IDLE', 'ERROR'],
  PROCESSING: ['READY', 'SPEAKING', 'IDLE', 'ERROR'],
  SPEAKING: ['READY', 'IDLE', 'ERROR'],
  ERROR: ['READY', 'IDLE'],
};

type Listener = (state: AssistantState, previous: AssistantState) => void;

/**
 * Minimal internal state model. Deliberately not a generic FSM library —
 * just enough to gate valid transitions and notify listeners so UI state
 * stays in sync with SDK lifecycle/mic/output/audio events.
 */
export class StateMachine {
  private current: AssistantState = 'IDLE';
  private listeners = new Set<Listener>();

  getState(): AssistantState {
    return this.current;
  }

  canTransition(next: AssistantState): boolean {
    return TRANSITIONS[this.current].includes(next);
  }

  transition(next: AssistantState): void {
    if (!this.canTransition(next)) {
      throw new Error(`Invalid state transition: ${this.current} -> ${next}`);
    }
    const previous = this.current;
    this.current = next;
    for (const listener of this.listeners) {
      listener(next, previous);
    }
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  reset(): void {
    this.current = 'IDLE';
    this.listeners.clear();
  }
}
